/*
 * TEABALLS · Black Month 2026 — campaign behaviour (global, deferred)
 *
 * Offer truth comes only from the Promotion Center engine (window.TeaballsPromotion):
 * a percentage is shown when an active, confirmed campaign with a complete, own
 * eligibility list is running. Unknown eligibility = neutral copy, never a saving claim.
 * The offer preview (#TbBmOfferPreview) exists only in the theme editor / an unpublished
 * theme (snippets/teaballs-bm-head.liquid) and is flagged on screen.
 *
 * DOM contract
 *   [data-tb-offer-scope="global" | "product:<id>"]  [data-tb-offer-surface="<campaign surface>"]
 *     → data-tb-offer-state = neutral | offer | excluded
 *     [data-tb-offer-when="neutral offer …"]  hidden unless the state is listed
 *     [data-tb-percent]                        receives the percentage
 *   product-card[data-product-id]  → .tb-bm-badge for eligible products (surface "productCards")
 *
 * API: window.TeaballsBlackMonth.offer(surface) / .refresh()
 */
(() => {
  if (window.TeaballsBlackMonth) {
    window.TeaballsBlackMonth.refresh();
    return;
  }

  const PREVIEW_ID = 'TbBmOfferPreview';
  const BADGE_CLASS = 'tb-bm-badge';
  const FLAG_CLASS = 'tb-bm-preview-flag';
  const root = document.documentElement;

  const surfaces = () => (root.dataset.tbIdentitySurfaces || '').split(' ').filter(Boolean);
  const identityVisible = () => surfaces().length > 0;

  const readPreview = () => {
    const element = document.getElementById(PREVIEW_ID);
    if (!element) return null;
    try {
      const data = JSON.parse(element.textContent);
      const percent = Number(data.percent) || 0;
      if (percent <= 0) return null;
      return { percent, ids: new Set((data.productIds || []).map(String)) };
    } catch (error) {
      console.warn('[TEABALLS Black Month] Offer preview config unreadable.', error);
      return null;
    }
  };

  const preview = readPreview();
  let usedPreview = false;

  const NEUTRAL = Object.freeze({ active: false, percent: 0, preview: false, eligible: () => false });

  /* the single offer resolver used by every campaign component */
  const offer = (surface) => {
    const bf = window.TeaballsBFPricing;
    if (bf?.active()) return {active:true,percent:25,preview:false,eligible:id=>bf.productRate(id)>0,rate:id=>bf.productSaving(id)};
    return NEUTRAL;
  };

  const applyScopes = () => {
    for (const scope of document.querySelectorAll('[data-tb-offer-scope]')) {
      const current = offer(scope.dataset.tbOfferSurface || '');
      const target = scope.dataset.tbOfferScope || 'global';
      let state = 'neutral';
      if (current.active) {
        state = target.startsWith('product:') ? (current.eligible(target.slice(8)) ? 'offer' : 'excluded') : 'offer';
        if (current.preview) usedPreview = true;
      }
      if (scope.dataset.tbOfferState !== state) scope.dataset.tbOfferState = state;
      for (const part of scope.querySelectorAll('[data-tb-offer-when]')) {
        const hide = !part.dataset.tbOfferWhen.split(' ').includes(state);
        if (part.hidden !== hide) part.hidden = hide;
      }
      const text = current.active ? String(target.startsWith('product:') ? current.rate(target.slice(8)) : current.percent) : '';
      for (const output of scope.querySelectorAll('[data-tb-percent]')) {
        if (output.textContent !== text) output.textContent = text;
      }
    }
  };

  const createBadge = (percent) => {
    const badge = document.createElement('span');
    badge.className = BADGE_CLASS;
    badge.setAttribute('aria-hidden', 'true');
    const lockup = window.TbBmAssets?.compactDark;
    if (lockup) {
      const image = document.createElement('img');
      image.className = `${BADGE_CLASS}__lockup`;
      image.src = lockup;
      image.alt = '';
      image.width = 52;
      image.height = 35;
      image.decoding = 'async';
      badge.append(image);
    }
    const value = document.createElement('span');
    value.className = `${BADGE_CLASS}__value`;
    value.textContent = `–${percent} %`;
    badge.append(value);
    const basis=document.createElement('small');basis.className=BADGE_CLASS+'__basis';basis.textContent='ggü. Einzelkauf';basis.hidden=true;badge.append(basis);
    return badge;
  };

  const applyBadges = () => {
    const current = offer('productBadges');
    const on = current.active && surfaces().includes('productCards');
    for (const card of document.querySelectorAll('product-card[data-product-id]')) {
      const want = on && current.eligible(card.dataset.productId);
      const badge = card.querySelector(`:scope > .${BADGE_CLASS}`);
      if (want) {
        if (current.preview) usedPreview = true;
        if (!badge) card.prepend(createBadge(current.rate(card.dataset.productId)));
        else {
          const value = badge.querySelector(`.${BADGE_CLASS}__value`);
          const text = `–${current.rate(card.dataset.productId)} %`;
          if (value && value.textContent !== text) value.textContent = text;
        }
        const currentBadge=card.querySelector(`:scope > .${BADGE_CLASS}`);
        const isSet=Boolean(window.TeaballsBFPricing?.setProduct(card.dataset.productId));
        const basis=currentBadge?.querySelector('.'+BADGE_CLASS+'__basis');if(basis)basis.hidden=!isSet;
        /* visually hidden saving hint for assistive technology, next to the price */
        if (card.dataset.tbBmLabelled === undefined) {
          card.dataset.tbBmLabelled = '';
          const note = document.createElement('span');
          note.className = 'visually-hidden';
          note.dataset.tbBmNote = '';
          note.textContent = `Black Month: ${current.rate(card.dataset.productId)} % ${isSet ? 'Ersparnis gegenüber regulärem Einzelkauf' : 'Aktionsvorteil'}`;
          card.append(note);
        }
      } else {
        badge?.remove();
        if (card.dataset.tbBmLabelled !== undefined) {
          card.querySelector(':scope > [data-tb-bm-note]')?.remove();
          delete card.dataset.tbBmLabelled;
        }
      }
    }
  };

  const applyFlag = () => {
    const existing = document.querySelector(`.${FLAG_CLASS}`);
    if (usedPreview && identityVisible()) {
      if (existing) return;
      const flag = document.createElement('div');
      flag.className = FLAG_CLASS;
      flag.setAttribute('role', 'note');
      flag.textContent = `Angebots-Vorschau ${preview.percent} % · nicht live`;
      document.body.append(flag);
    } else {
      existing?.remove();
    }
  };

  let frame = 0;
  const refresh = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      usedPreview = false;
      applyScopes();
      applyBadges();
      applyFlag();
    });
  };

  for (const type of ['teaballs:promotion:ready', 'teaballs:promotion:change', 'teaballs:bf-pricing:change']) {
    document.addEventListener(type, refresh);
  }

  /* product grids (filters, pagination, recommendations, quick add) and re-rendered drawers */
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches('product-card, [data-tb-offer-scope]') || node.querySelector('product-card, [data-tb-offer-scope]')) {
          refresh();
          return;
        }
      }
    }
  });

  /* identity switches on/off while the page is open (engine updates the root attribute) */
  const rootObserver = new MutationObserver(refresh);

  const start = () => {
    observer.observe(document.body, { childList: true, subtree: true });
    rootObserver.observe(root, { attributes: true, attributeFilter: ['data-tb-identity-surfaces'] });
    refresh();
  };

  window.TeaballsBlackMonth = { version: '1.0.0', offer, refresh };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
